import React, { useEffect, useState } from 'react';
import { getDailySummary, getTopProducts, getHourlySales } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { 
  CalendarIcon, Download, Loader2, RefreshCw, BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

async function fetchReportData(selectedDate) {
  const dateStr = format(selectedDate, 'yyyyMMdd');
  const [summaryData, products, hourly] = await Promise.all([
    getDailySummary(dateStr),
    getTopProducts(dateStr, 20),
    getHourlySales(dateStr),
  ]);

  return { summaryData, products, hourly };
}

export default function AdminReports() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [hourlySales, setHourlySales] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const applyReportData = ({ summaryData, products, hourly }) => {
    setSummary(summaryData);
    setTopProducts(products);
    setHourlySales(hourly);
  };

  useEffect(() => {
    let cancelled = false;

    async function loadForDate() {
      setIsLoading(true);
      try {
        const nextData = await fetchReportData(selectedDate);
        if (!cancelled) {
          applyReportData(nextData);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading reports:', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadForDate();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      applyReportData(await fetchReportData(selectedDate));
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Artikel', 'Menge', 'Umsatz'],
      ...topProducts.map(p => [p.product_name, p.quantity, p.total.toFixed(2)])
    ];
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bericht-${format(selectedDate, 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const maxHourlySales = Math.max(...Object.values(hourlySales).map(h => h.total || 0), 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-reports">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Berichte</h1>
          <p className="text-slate-500">
            {format(selectedDate, 'dd. MMMM yyyy', { locale: de })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-48">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(selectedDate, 'dd.MM.yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={de}
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" onClick={loadReportData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={exportCSV} className="bg-[#1a2f38]">
            <Download className="w-4 h-4 mr-2" />
            CSV Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Bruttoumsatz</p>
            <p className="text-2xl font-bold text-green-700">€{summary?.total_sales?.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Nettoumsatz</p>
            <p className="text-2xl font-bold text-blue-700">€{summary?.net_sales?.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Bestellungen</p>
            <p className="text-2xl font-bold text-orange-700">{summary?.total_orders}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Rabatte</p>
            <p className="text-2xl font-bold text-purple-700">€{summary?.discount_total?.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* VAT Breakdown */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>MwSt-Aufschlüsselung</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary?.vat_breakdown || {}).map(([rate, amount]) => (
              <div key={rate} className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">MwSt {rate}%</p>
                <p className="text-xl font-bold">€{amount?.toFixed(2)}</p>
              </div>
            ))}
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-slate-500">Barzahlung</p>
              <p className="text-xl font-bold text-green-700">€{summary?.cash_total?.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-slate-500">Kartenzahlung</p>
              <p className="text-xl font-bold text-blue-700">€{summary?.card_total?.toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hourly Sales Chart */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Umsatz nach Stunde
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-40">
            {Object.entries(hourlySales).map(([hour, data]) => {
              const height = (data.total / maxHourlySales) * 100;
              return (
                <div key={hour} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-[#1a2f38] rounded-t transition-all hover:bg-[#2a4a56]"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${hour}:00 - €${data.total?.toFixed(2)} (${data.orders} Bestellungen)`}
                  />
                  <span className="text-xs text-slate-400 mt-1">{hour}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Top Artikel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {topProducts.map((product, index) => (
              <div key={product.product_id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="font-medium">{product.product_name}</span>
                </div>
                <div className="flex items-center gap-8">
                  <span className="text-slate-500">{product.quantity}x</span>
                  <span className="font-bold tabular-nums w-24 text-right">€{product.total?.toFixed(2)}</span>
                </div>
              </div>
            ))}
            {topProducts.length === 0 && (
              <p className="text-center py-8 text-slate-400">Keine Verkäufe an diesem Tag</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
